import {X} from 'lucide-react';
import * as React from 'react';

type Props = {
  fieldName: string;
  value: string;
  isEditing: boolean;
  onCommit: (value: string) => void;
  onStartEdit: () => void;
  onRemove: () => void;
};

export const TextFieldPill = (props: Props) => {
  const {fieldName, value, isEditing, onCommit, onStartEdit, onRemove} = props;
  const [editValue, setEditValue] = React.useState(value);
  const inputRef = React.useRef<HTMLInputElement | null>(null);

  React.useEffect(() => {
    if (isEditing) {
      setEditValue(value);
      // Focus after render
      requestAnimationFrame(() => {
        inputRef.current?.focus();
      });
    }
  }, [isEditing, value]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      onCommit(editValue);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      if (!value) {
        // If no value was set yet, remove the field
        onRemove();
      } else {
        // Revert to original value
        onCommit(value);
      }
    }
  };

  if (isEditing) {
    return (
      <span className="bg-primary/10 text-primary inline-flex h-6 items-center gap-1 rounded-md px-2 text-xs">
        <span className="text-primary/70">{fieldName}:</span>
        <input
          ref={inputRef}
          type="text"
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={() => onCommit(editValue)}
          className="w-20 min-w-0 border-none bg-transparent text-xs font-medium outline-none"
          placeholder="type value..."
        />
        <button
          type="button"
          onClick={onRemove}
          className="ml-0.5 rounded hover:bg-primary/20"
        >
          <X className="h-3 w-3" />
        </button>
      </span>
    );
  }

  return (
    <span className="bg-primary/10 text-primary inline-flex h-6 items-center gap-1 rounded-md px-2 text-xs">
      <span className="text-primary/70">{fieldName}:</span>
      <button
        type="button"
        onClick={onStartEdit}
        className="font-medium hover:underline"
      >
        {value}
      </button>
      <button
        type="button"
        onClick={onRemove}
        className="ml-0.5 rounded hover:bg-primary/20"
      >
        <X className="h-3 w-3" />
      </button>
    </span>
  );
};
